import React, { useState } from "react";
import { ExternalLink, LoaderCircle } from "lucide-react";
import { apiFetch } from "@/lib/api";

export default function ArtifactPreview({ artifact, mission, compact = false }) {
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const verified = (mission.tasks || []).some(task =>
    task.status === "verified" && task.verified === true &&
    task.result?.artifact_id === artifact.artifact_id);
  const show = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (preview) return;
    setBusy(true);
    try {
      const result = await apiFetch("/mission/artifacts/" +
        encodeURIComponent(artifact.artifact_id) + "/preview");
      setPreview(result);
      setError("");
    } catch (e) { setError(e.message || "Preview unavailable"); }
    finally { setBusy(false); }
  };
  return <div className={compact ? "space-y-2 mt-2" : "border border-line bg-surface-2 rounded-xl p-3 space-y-2"} data-testid="earnos-inline-artifact">
    <div className="flex items-start justify-between gap-2">
      {!compact ? <div className="min-w-0">
        <p className="font-semibold text-sm text-ink break-words">{artifact.title}</p>
        <p className="text-xs text-ink-2">{verified ? "File/content checked" : "Prepared · verification pending"}. {artifact.public_path ? "Public website link available." : "Not proof of an income result."}</p>
      </div> : null}
      <button type="button" onClick={show} disabled={busy}
        className="shrink-0 rounded-lg border border-brand/40 px-3 py-2 text-xs font-bold text-brand-light">
        {open ? "Hide" : "Read here"}
      </button>
    </div>
    {error ? <p role="alert" className="text-xs text-danger">{error}</p> : null}
    {busy ? <p className="text-xs text-ink-2 flex gap-2"><LoaderCircle size={14} className="animate-spin"/> Opening result…</p> : null}
    {open && preview ? <div className="border-t border-line pt-3 space-y-3">
      {preview.kind === "document" ?
        <div className="text-sm text-ink whitespace-pre-wrap break-words leading-6 max-h-[55dvh] overflow-y-auto" data-testid="inline-document-content">{preview.text || "This document is empty."}{preview.truncated ? "\n\nPreview shortened. Open Mission Control for the original." : ""}</div> :
      preview.kind === "presentation" ?
        <div className="space-y-3 max-h-[55dvh] overflow-y-auto">{(preview.slides || []).map((slide, i) =>
          <section key={i} className="rounded-lg border border-line p-3">
            <h4 className="font-bold text-ink">{i + 1}. {slide.title}</h4>
            {(slide.points || []).map((point, n) => <p key={n} className="text-sm text-ink-2 mt-1">• {point}</p>)}
          </section>)}</div> :
      preview.kind === "offer_website" ?
        <div className="space-y-3 text-sm text-ink max-h-[55dvh] overflow-y-auto">
          <p className="text-xs uppercase font-bold text-brand-light">{preview.offer?.brand}</p>
          <h4 className="text-xl font-extrabold">{preview.offer?.headline}</h4>
          <p>{preview.offer?.subheadline}</p>
          <p className="font-bold">{preview.offer?.offer_title} · {preview.offer?.price_text}</p>
          <p>{preview.offer?.offer_description}</p>
          {(preview.offer?.benefits || []).map((x, i) => <p key={i}>✓ {x}</p>)}
          <p className="text-xs text-ink-2">Approved call to action: {preview.offer?.cta_label} — {preview.offer?.cta_url}</p>
          {preview.public_path ? <a href={preview.public_path} rel="noopener noreferrer" target="_blank" className="inline-flex gap-1 text-brand-light font-bold">Open published page <ExternalLink size={15}/></a> :
            <p className="text-xs text-ink-2">Private draft. EARNOS must request your permission before publication.</p>}
        </div> : null}
      <p className="text-xs text-ink-2">Viewing this content doesn't mean it was posted, delivered to buyers, or paid.</p>
    </div> : null}
  </div>;
}
